/**
 * Multiple Choice Activity Slide
 * Single-select quiz question
 */

import { useMemo } from 'react';
import { Check, Sparkles } from 'lucide-react';
import { motion } from 'motion/react';
import type { ActivitySlideProps } from '../types';
import { createDeterministicShuffledOrder } from '../../../../utils/slides/shuffle';

const OPTION_COLORS = [
  { bg: 'bg-blue-500', bgLight: 'bg-blue-500/10', border: 'border-blue-500', ring: 'ring-blue-500/30', text: 'text-blue-600', hoverBg: 'hover:bg-blue-50' },
  { bg: 'bg-orange-500', bgLight: 'bg-orange-500/10', border: 'border-orange-500', ring: 'ring-orange-500/30', text: 'text-orange-600', hoverBg: 'hover:bg-orange-50' },
  { bg: 'bg-emerald-500', bgLight: 'bg-emerald-500/10', border: 'border-emerald-500', ring: 'ring-emerald-500/30', text: 'text-emerald-600', hoverBg: 'hover:bg-emerald-50' },
  { bg: 'bg-purple-500', bgLight: 'bg-purple-500/10', border: 'border-purple-500', ring: 'ring-purple-500/30', text: 'text-purple-600', hoverBg: 'hover:bg-purple-50' },
  { bg: 'bg-pink-500', bgLight: 'bg-pink-500/10', border: 'border-pink-500', ring: 'ring-pink-500/30', text: 'text-pink-600', hoverBg: 'hover:bg-pink-50' },
  { bg: 'bg-cyan-500', bgLight: 'bg-cyan-500/10', border: 'border-cyan-500', ring: 'ring-cyan-500/30', text: 'text-cyan-600', hoverBg: 'hover:bg-cyan-50' },
];

export default function MultipleChoiceSlide({
  slide,
  selectedAnswer,
  onAnswerChange,
  hasSubmitted,
  shuffleSeed,
  className = '',
}: ActivitySlideProps) {
  const { content } = slide;
  const options: string[] = content?.options || [];
  const displayOptions = useMemo(() => {
    const order = content?.shuffleOptions
      ? createDeterministicShuffledOrder(options.length, `${shuffleSeed ?? 'default'}:${slide.id}:multiple-choice-options`)
      : options.map((_, index) => index);

    return order.map((originalIndex) => ({
      originalIndex,
      label: options[originalIndex],
    }));
  }, [content?.shuffleOptions, options, shuffleSeed, slide.id]);

  return (
    <div className={`max-w-3xl mx-auto mb-8 ${className}`}>
      {/* Activity type badge */}
      <div className="flex items-center justify-center mb-6">
        <div className="inline-flex items-center gap-2 bg-blue-500/10 text-blue-600 px-4 py-1.5 rounded-full text-sm font-semibold">
          <Sparkles className="w-4 h-4" />
          Choose the correct answer
        </div>
      </div>

      {/* Options grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {displayOptions.map(({ originalIndex, label }, index: number) => {
          const color = OPTION_COLORS[index % OPTION_COLORS.length];
          const isSelected = selectedAnswer === originalIndex;

          return (
            <motion.button
              key={originalIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.08, duration: 0.3 }}
              whileHover={!hasSubmitted ? { scale: 1.03, y: -2 } : {}}
              whileTap={!hasSubmitted ? { scale: 0.97 } : {}}
              onClick={() => !hasSubmitted && onAnswerChange(originalIndex)}
              disabled={hasSubmitted}
              className={`relative p-5 sm:p-6 rounded-2xl border-2 text-left transition-all duration-200 group overflow-hidden ${
                isSelected
                  ? `${color.border} ${color.bgLight} shadow-lg ring-4 ${color.ring}`
                  : `border-border/60 bg-card ${color.hoverBg} hover:shadow-md`
              } ${hasSubmitted ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {/* Selection pulse effect */}
              {isSelected && (
                <motion.div
                  initial={{ scale: 0, opacity: 0.5 }}
                  animate={{ scale: 2.5, opacity: 0 }}
                  transition={{ duration: 0.6 }}
                  className={`absolute inset-0 ${color.bg} rounded-2xl`}
                />
              )}

              <div className="relative flex items-center gap-4">
                {/* Letter badge with color */}
                <div
                  className={`w-12 h-12 rounded-2xl flex items-center justify-center text-lg font-extrabold transition-all shrink-0 ${
                    isSelected
                      ? `${color.bg} text-white shadow-lg`
                      : `bg-muted/80 ${color.text}`
                  }`}
                >
                  {String.fromCharCode(65 + index)}
                </div>

                {/* Option text */}
                <div className="flex-1 font-medium text-base sm:text-lg leading-snug">
                  {label}
                </div>

                {/* Check indicator */}
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                    className={`w-8 h-8 rounded-full ${color.bg} flex items-center justify-center shrink-0`}
                  >
                    <Check className="w-5 h-5 text-white" strokeWidth={3} />
                  </motion.div>
                )}
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Selection hint */}
      {!hasSubmitted && selectedAnswer === null && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-sm text-muted-foreground mt-5 animate-pulse"
        >
          Tap an option to select your answer
        </motion.p>
      )}
    </div>
  );
}
